// src/components/marketing/SiteNav.js
// Header matching the legacy site exactly: serif SQUARE FARE wordmark on the
// left; uppercase, letter-spaced nav on the right (ORDER / CONTACT US / ABOUT
// dropdowns + ACCOUNT + cart). ACCOUNT opens the Auth0 popup — the legacy-site
// login now lives in the footer.

import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useAuthActions } from 'components/auth/AuthActions';
import { useFlow } from 'context/FlowContext';
import { useMediaQuery } from 'hooks/useMediaQuery';
import { COLORS, FONTS, NAV_GROUPS } from 'styles/theme';
import AccountPanel from 'components/auth/AccountPanel';

// From the nav's measured width, not the shared 1024px layout breakpoint.
const COMPACT_NAV_QUERY = '(max-width: 960px)';

const NAV_LINK = {
  fontFamily: FONTS.sans, textTransform: 'uppercase', letterSpacing: '1.5px',
  fontSize: '14px', fontWeight: 700, color: COLORS.dark, textDecoration: 'none',
  background: 'none', border: 'none', cursor: 'pointer', padding: '10px 0',
};

function NavDropdown({ group }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const btnRef = useRef(null);
  const itemStyle = {
    display: 'block', padding: '7px 0', textDecoration: 'none',
    color: COLORS.dark, fontFamily: FONTS.sans, fontSize: '15px', fontWeight: 700,
    textTransform: 'uppercase', letterSpacing: '1.5px', whiteSpace: 'nowrap',
    textAlign: 'right', background: 'none', border: 'none', cursor: 'pointer',
  };

  // A tap outside is the only way to dismiss the panel on touch, where there is no mouseleave.
  useEffect(() => {
    if (!open) return undefined;
    const onDocDown = e => { if (!wrapRef.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDocDown);
    return () => document.removeEventListener('mousedown', onDocDown);
  }, [open]);

  return (
    <div
      ref={wrapRef}
      style={{ position: 'relative' }}
      // Mouse only: on touch this fires too and would fight the click that follows.
      onPointerEnter={e => { if (e.pointerType === 'mouse') setOpen(true); }}
      onPointerLeave={e => { if (e.pointerType === 'mouse') setOpen(false); }}
      onKeyDown={e => { if (e.key === 'Escape' && open) { setOpen(false); btnRef.current?.focus(); } }}
      onBlur={e => { if (!e.currentTarget.contains(e.relatedTarget)) setOpen(false); }}
    >
      <button
        ref={btnRef}
        style={NAV_LINK}
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => setOpen(o => !o)}
      >
        {group.label}
      </button>
      {/* Hidden, not unmounted: four of these pages are linked nowhere else, and a crawler never hovers. */}
      <div style={{
        display: open ? 'block' : 'none',
        position: 'absolute', top: '100%', right: 0, background: '#fff',
        boxShadow: '0 10px 30px rgba(0,0,0,0.10)', padding: '22px 26px',
        minWidth: '250px', zIndex: 50,
      }}>
        {group.items.map(item => (
          item.to
            ? <Link key={item.label} to={item.to} style={itemStyle} onClick={() => setOpen(false)}>{item.label}</Link>
            : <a key={item.label} href={item.href} style={itemStyle} onClick={() => setOpen(false)}>{item.label}</a>
        ))}
      </div>
    </div>
  );
}

/**
 * @param {function} [onCartClick] Storefront pages render a slide-out cart
 *   drawer, so they pass a handler and the cart button opens it. Marketing pages
 *   have no drawer mounted, so without a handler the button navigates instead.
 *   This is the only behavioural difference that justified a second header.
 */
export default function SiteNav({ onCartClick }) {
  const { handleLogin, busy, justLoggedIn, clearJustLoggedIn, signOutCleanup } = useAuthActions();
  const { isAuthenticated, logout } = useAuth0();
  const { cartCount } = useFlow();
  const navigate = useNavigate();
  const location = useLocation();
  const [accountOpen, setAccountOpen] = useState(false);
  const compact = useMediaQuery(COMPACT_NAV_QUERY);
  const [menuOpen, setMenuOpen] = useState(false);
  // Which nav group's accordion is expanded in the mobile menu. Hover doesn't
  // exist on touch, so the desktop mouse-over dropdowns become tap-to-expand.
  const [openGroup, setOpenGroup] = useState(null);
  const menuPanelRef = useRef(null);
  const menuBtnRef = useRef(null);

  // Following a link should put the menu away — without this it sat open over
  // the top of the page you just navigated to.
  useEffect(() => {
    setMenuOpen(false);
    setOpenGroup(null);
  }, [location.pathname]);

  // Crossing back above the breakpoint should drop the menu too, or it
  // reappears with no tap if the viewport narrows again later.
  useEffect(() => {
    if (!compact) {
      setMenuOpen(false);
      setOpenGroup(null);
    }
  }, [compact]);

  // Escape or a tap outside the panel dismisses it, matching NavDropdown.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDocDown = e => {
      if (!menuPanelRef.current?.contains(e.target) && !menuBtnRef.current?.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    const onKeyDown = e => {
      if (e.key === 'Escape') {
        setMenuOpen(false);
        menuBtnRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onDocDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen]);

  // Fresh login resolved (no redirect) → open the account panel right here.
  useEffect(() => {
    if (justLoggedIn) {
      clearJustLoggedIn();
      setAccountOpen(true);
    }
  }, [justLoggedIn, clearJustLoggedIn]);

  // Logged in → open the account panel; logged out → Auth0 popup.
  const onAccountClick = () => {
    setMenuOpen(false);
    if (isAuthenticated) setAccountOpen(true);
    else handleLogin();
  };

  return (
    <header style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '26px 4%', flexWrap: 'wrap', gap: '16px',
      position: 'sticky', top: 0, background: COLORS.cream, zIndex: 40,
      boxShadow: '0 1px 0 rgba(0,0,0,0.05)',
    }}>
      <Link to="/" style={{
        fontFamily: FONTS.serif, fontSize: '30px', fontWeight: 600, letterSpacing: '2px',
        color: COLORS.dark, textDecoration: 'none',
      }}>
        SQUARE FARE
      </Link>

      <nav style={{ display: 'flex', alignItems: 'center', gap: compact ? '10px' : '38px', flexWrap: 'wrap' }}>
        {!compact && NAV_GROUPS.map(group => <NavDropdown key={group.label} group={group} />)}

        {!compact && (
          <button onClick={onAccountClick} disabled={busy} style={{ ...NAV_LINK, opacity: busy ? 0.6 : 1 }}>
            {busy ? 'Opening…' : (isAuthenticated ? 'Account' : 'Login')}
          </button>
        )}

        {/* The cart stays outside the hamburger — it's the one thing a customer
            mid-order reaches for, and burying it a tap deeper loses orders. */}
        <button
          onClick={() => (onCartClick ? onCartClick() : navigate(cartCount > 0 ? '/checkout' : '/this-weeks-menu'))}
          aria-label={`Cart, ${cartCount} items`}
          style={{ position: 'relative', background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={COLORS.dark}
            strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="9" cy="21" r="1.4" fill={COLORS.dark} />
            <circle cx="19" cy="21" r="1.4" fill={COLORS.dark} />
            <path d="M1 1h4l2.6 13.4a2 2 0 0 0 2 1.6h8.9a2 2 0 0 0 2-1.6L23 6H6" />
          </svg>
          {cartCount > 0 && (
            <span style={{
              position: 'absolute', top: '-4px', right: '-6px', background: COLORS.orange,
              color: '#fff', fontSize: '11px', fontWeight: 700, borderRadius: '50%',
              minWidth: '17px', height: '17px', display: 'inline-flex',
              alignItems: 'center', justifyContent: 'center', fontFamily: FONTS.sans,
            }}>{cartCount}</span>
          )}
        </button>

        {compact && (
          <button
            ref={menuBtnRef}
            onClick={() => setMenuOpen((v) => !v)}
            aria-expanded={menuOpen}
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '6px 2px 6px 8px' }}
          >
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke={COLORS.dark}
              strokeWidth="2" strokeLinecap="round">
              {menuOpen
                ? <><path d="M5 5l14 14" /><path d="M19 5L5 19" /></>
                : <><path d="M3 6h18" /><path d="M3 12h18" /><path d="M3 18h18" /></>}
            </svg>
          </button>
        )}
      </nav>

      {compact && menuOpen && (
        <div ref={menuPanelRef} style={{
          // Anchored to the sticky header, so it stays put while the page
          // behind scrolls; its own overflow handles long menus on short
          // landscape screens.
          position: 'absolute', top: '100%', left: 0, right: 0,
          background: COLORS.cream, borderTop: `1px solid ${COLORS.creamAlt}`,
          boxShadow: '0 14px 30px rgba(0,0,0,0.12)', zIndex: 45,
          maxHeight: 'calc(100vh - 88px)', overflowY: 'auto',
          padding: '6px 6% 18px',
        }}>
          {NAV_GROUPS.map((group) => {
            const expanded = openGroup === group.label;
            return (
              <div key={group.label} style={{ borderBottom: `1px solid ${COLORS.creamAlt}` }}>
                <button
                  onClick={() => setOpenGroup(expanded ? null : group.label)}
                  aria-expanded={expanded}
                  style={{
                    ...NAV_LINK, width: '100%', display: 'flex', alignItems: 'center',
                    justifyContent: 'space-between', padding: '15px 0',
                  }}
                >
                  {group.label}
                  <span aria-hidden="true" style={{
                    fontSize: '12px', transition: 'transform 0.15s',
                    transform: expanded ? 'rotate(180deg)' : 'none',
                  }}>▼</span>
                </button>
                {expanded && (
                  <div style={{ padding: '0 0 10px' }}>
                    {group.items.map(item => {
                      const itemStyle = {
                        display: 'block', padding: '11px 0 11px 14px', textDecoration: 'none',
                        color: COLORS.text, fontFamily: FONTS.sans, fontSize: '15px',
                        fontWeight: 600, background: 'none', border: 'none',
                      };
                      return item.to
                        ? <Link key={item.label} to={item.to} style={itemStyle} onClick={() => setMenuOpen(false)}>{item.label}</Link>
                        : <a key={item.label} href={item.href} style={itemStyle} onClick={() => setMenuOpen(false)}>{item.label}</a>;
                    })}
                  </div>
                )}
              </div>
            );
          })}

          <button
            onClick={onAccountClick}
            disabled={busy}
            style={{
              ...NAV_LINK, width: '100%', textAlign: 'left', padding: '15px 0',
              opacity: busy ? 0.6 : 1,
            }}
          >
            {busy ? 'Opening…' : (isAuthenticated ? 'Account' : 'Login')}
          </button>
        </div>
      )}

      <AccountPanel
        open={accountOpen}
        onClose={() => setAccountOpen(false)}
        onSignOut={() => {
          setAccountOpen(false);
          signOutCleanup();
          logout({ logoutParams: { returnTo: window.location.origin } });
        }}
      />
    </header>
  );
}
